'use client'

import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import VideoThumbnailImage from '@/components/content/videos/VideoThumbnailImage'

import { cn } from '@/lib/utils'
import { createLogger } from '@/lib/logger'
import {
  type SegmentPlayRequest,
  type SegmentPlayerApi,
  SegmentPlayerProvider,
} from './SegmentPlayerContext'
import { Icon } from '@/icons/Icon'
import { getThumbnail } from '@/components/content/videos/helpers'
import type { Media } from '@/payload-types'

const logger = createLogger('SegmentPlayer')

// Minimal YouTube IFrame API types - we only use a handful of methods.
type YouTubePlayer = {
  loadVideoById: (opts: { videoId: string; startSeconds?: number; endSeconds?: number }) => void
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void
  playVideo: () => void
  pauseVideo: () => void
  mute: () => void
  unMute: () => void
  getCurrentTime: () => number
  destroy: () => void
}

type YouTubeApi = {
  Player: new (
    element: HTMLElement | string,
    options: {
      videoId?: string
      width?: string | number
      height?: string | number
      playerVars?: Record<string, string | number>
      events?: {
        onReady?: (event: { target: YouTubePlayer }) => void
        onStateChange?: (event: { data: number; target: YouTubePlayer }) => void
      }
    },
  ) => YouTubePlayer
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    YT?: YouTubeApi
    onYouTubeIframeAPIReady?: () => void
  }
}

let youtubeApiPromise: Promise<YouTubeApi> | null = null

function loadYouTubeApi(): Promise<YouTubeApi> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Cannot load YouTube API server-side'))
  }
  if (window.YT?.Player) return Promise.resolve(window.YT)
  if (youtubeApiPromise) return youtubeApiPromise

  youtubeApiPromise = new Promise<YouTubeApi>((resolve, reject) => {
    // Reset the cached promise on any failure so a later click retries the load
    // instead of getting a permanently-rejected promise for the whole session.
    const fail = (error: Error) => {
      youtubeApiPromise = null
      reject(error)
    }
    const previousCallback = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.()
      if (window.YT?.Player) {
        resolve(window.YT)
      } else {
        fail(new Error('YT API loaded but Player constructor missing'))
      }
    }

    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    script.onerror = () => fail(new Error('Failed to load YouTube IFrame API script'))
    document.body.appendChild(script)
  })

  return youtubeApiPromise
}

// Resize bounds (desktop only). Height is always derived 16:9 from width, so we
// only track width. MAX is additionally clamped to the viewport at drag time.
const MIN_PLAYER_WIDTH = 280
const MAX_PLAYER_WIDTH = 720
const DEFAULT_PLAYER_WIDTH = 360
const PLAYER_WIDTH_STORAGE_KEY = 'segmentPlayerWidth'

export default function SegmentPlayer({ children }: { children: ReactNode }) {
  const [currentSegment, setCurrentSegment] = useState<SegmentPlayRequest | null>(null)
  const [isMinimized, setIsMinimized] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [isPlayerReady, setIsPlayerReady] = useState(false)
  const [playerWidth, setPlayerWidth] = useState(DEFAULT_PLAYER_WIDTH)
  const [isResizing, setIsResizing] = useState(false)
  // Default true so the SSR/first render matches desktop (the popout only ever
  // appears after a client click, by which point matchMedia has corrected it).
  const [isDesktop, setIsDesktop] = useState(true)
  // Upper bound for the rendered width = viewport minus margins. Keeps the
  // resizable player from overflowing a narrow window/viewport even if a wider
  // width was persisted on a bigger screen.
  const [maxAllowedWidth, setMaxAllowedWidth] = useState(MAX_PLAYER_WIDTH)

  // Refs (don't trigger re-renders, but must stay accurate across closures).
  const playerHostRef = useRef<HTMLDivElement | null>(null)
  // The stable outer node permanently portaled to document.body and
  // repositioned in place (never moved in the DOM) - see the rAF sync effect
  // below for why.
  const playerWrapperRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<YouTubePlayer | null>(null)
  const currentYoutubeIdRef = useRef<string | null>(null)
  const currentEndSecondsRef = useRef<number | null>(null)
  const pendingSegmentRef = useRef<SegmentPlayRequest | null>(null)
  const resizeStartRef = useRef<{ startX: number; startWidth: number }>({
    startX: 0,
    startWidth: DEFAULT_PLAYER_WIDTH,
  })
  // Kept current so the onStateChange closure (created at player init time)
  // always reads the latest value without needing to recreate the player.
  const isDesktopRef = useRef(isDesktop)
  useEffect(() => {
    isDesktopRef.current = isDesktop
  }, [isDesktop])
  // Kept current so `play` (below) can check whether a segment is already
  // active without listing currentSegment as a dependency. If `play` depended
  // on currentSegment its identity would change on every segment, cascade
  // through the context api into VideoPlayer's autoplay effect, and cause an
  // infinite play loop.
  const currentSegmentRef = useRef<SegmentPlayRequest | null>(null)
  useEffect(() => {
    currentSegmentRef.current = currentSegment
  }, [currentSegment])

  const applySegment = useCallback((segment: SegmentPlayRequest) => {
    const player = playerRef.current
    if (!player) return

    currentEndSecondsRef.current = segment.endSeconds ?? null

    if (currentYoutubeIdRef.current === segment.youtubeId) {
      player.seekTo(segment.startSeconds, true)
      player.playVideo()
    } else {
      player.loadVideoById({
        videoId: segment.youtubeId,
        startSeconds: segment.startSeconds,
        endSeconds: segment.endSeconds,
      })
      currentYoutubeIdRef.current = segment.youtubeId
    }
  }, [])

  // When a segment is requested, ensure the player exists then apply.
  useEffect(() => {
    if (!currentSegment) return
    pendingSegmentRef.current = currentSegment

    if (playerRef.current) {
      // Player exists. The "subsequent segment changes" effect below (deps
      // include `currentSegment`) will call applySegment, so don't call it
      // here too - doing so previously caused a double-apply when switching
      // between videos: loadVideoById would update currentYoutubeIdRef, then
      // the second invocation took the same-video branch and called seekTo
      // immediately, racing the load and stranding the playhead at 0.
      // Same-video switches happened to be safe because both invocations
      // landed on the (idempotent) seekTo path.
      return
    }

    let cancelled = false
    loadYouTubeApi()
      .then((YT) => {
        if (cancelled || !playerHostRef.current) return
        playerRef.current = new YT.Player(playerHostRef.current, {
          width: '100%',
          height: '100%',
          videoId: currentSegment.youtubeId,
          playerVars: {
            autoplay: 1,
            mute: isMuted ? 1 : 0,
            start: Math.floor(currentSegment.startSeconds),
            ...(currentSegment.endSeconds !== undefined && {
              end: Math.ceil(currentSegment.endSeconds),
            }),
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
          },
          events: {
            onReady: (event) => {
              currentYoutubeIdRef.current = currentSegment.youtubeId
              currentEndSecondsRef.current = currentSegment.endSeconds ?? null
              setIsPlayerReady(true)
              event.target.playVideo()
              // If another segment was requested while the API was loading,
              // apply it now.
              const pending = pendingSegmentRef.current
              if (pending && pending.segmentId !== currentSegment.segmentId) {
                applySegment(pending)
              }
            },
            onStateChange: (event) => {
              // On mobile: collapse to minimised when paused, restore when playing.
              // YT states: 1 = playing, 2 = paused.
              if (!isDesktopRef.current) {
                if (event.data === 2) setIsMinimized(true)
                else if (event.data === 1) setIsMinimized(false)
              }
            },
          },
        })
      })
      .catch((error) => {
        logger.error('Failed to initialize YouTube player', error)
      })

    return () => {
      cancelled = true
    }
  }, [currentSegment?.segmentId])

  // Apply subsequent segment changes once player is alive.
  useEffect(() => {
    if (!currentSegment || !isPlayerReady) return
    if (!playerRef.current) return
    applySegment(currentSegment)
  }, [currentSegment, isPlayerReady, applySegment])

  // Keep the player's mute state in sync with the isMuted toggle.
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current) return
    if (isMuted) {
      playerRef.current.mute()
    } else {
      playerRef.current.unMute()
    }
  }, [isMuted, isPlayerReady])

  // Watch current time, pause at segment end. YouTube's `end` playerVar is
  // unreliable when seeking; this is the belt-and-suspenders pause.
  useEffect(() => {
    if (!isPlayerReady || !currentSegment) return
    const interval = window.setInterval(() => {
      const player = playerRef.current
      const endSeconds = currentEndSecondsRef.current
      if (!player || endSeconds === null) return
      try {
        const t = player.getCurrentTime()
        if (t >= endSeconds - 0.1) {
          player.pauseVideo()
        }
      } catch {
        // Player may be transitioning between videos; ignore.
      }
    }, 500)
    return () => window.clearInterval(interval)
  }, [isPlayerReady, currentSegment])

  const close = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.destroy()
      } catch (error) {
        logger.warn('Error destroying YouTube player', error)
      }
      playerRef.current = null
    }
    currentYoutubeIdRef.current = null
    currentEndSecondsRef.current = null
    pendingSegmentRef.current = null
    setIsPlayerReady(false)
    setIsMinimized(false)
    setCurrentSegment(null)
  }, [])

  // DOM nodes registered by VideoPlayer instances, keyed by their playerKey -
  // the candidate targets the live player can be portaled into inline instead
  // of the floating popout. Plain ref (not state): registration is imperative
  // and doesn't itself need to trigger a render.
  const anchorsRef = useRef<Map<string, HTMLElement>>(new Map())
  const registerAnchor = useCallback((key: string, element: HTMLElement | null) => {
    if (element) {
      anchorsRef.current.set(key, element)
    } else {
      anchorsRef.current.delete(key)
      // Anchor removed (route change, modal close, etc.) - don't stop
      // playback. inlineAnchor resolves to null and the player naturally
      // falls back to the floating popout, preserving seamless continuation
      // across page navigations.
    }
  }, [])

  // Tracks whether the active segment's registered anchor (if any) is on
  // screen. Drives the inline-vs-popout decision below. Optimistic default of
  // true: a play request is almost always triggered by clicking something
  // currently visible, and the observer corrects this within a frame if not.
  const [isAnchorVisible, setIsAnchorVisible] = useState(true)
  const activePlayerKey = currentSegment?.playerKey ?? null

  useLayoutEffect(() => {
    if (!activePlayerKey) {
      setIsAnchorVisible(false)
      return
    }
    const anchor = anchorsRef.current.get(activePlayerKey)
    if (!anchor) {
      setIsAnchorVisible(false)
      return
    }
    setIsAnchorVisible(true)
    const observer = new IntersectionObserver(
      ([entry]) => setIsAnchorVisible(entry.isIntersecting),
      { threshold: 0 },
    )
    observer.observe(anchor)
    return () => observer.disconnect()
  }, [activePlayerKey])

  // Target the live player portals into. The popout's video slot is always
  // mounted (just visually hidden) once a segment is active, so this never
  // dangles between "anchor scrolled away" and "popout slot ref attached" -
  // see the dialog's `hidden` toggling below instead of conditional mounting.
  const [popoutSlotEl, setPopoutSlotEl] = useState<HTMLDivElement | null>(null)
  const inlineAnchor = activePlayerKey ? anchorsRef.current.get(activePlayerKey) ?? null : null
  const showInline = Boolean(inlineAnchor) && isAnchorVisible
  const portalTarget = showInline ? inlineAnchor : popoutSlotEl

  // Mirrors portalTarget for the rAF loop below, which must read the latest
  // target every frame without restarting the loop on every render.
  const portalTargetRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    portalTargetRef.current = portalTarget
  }, [portalTarget])

  // The player never moves in the DOM - moving an <iframe> (or an ancestor
  // containing one), even via plain appendChild within the same document,
  // makes Chrome/most browsers reload it (a longstanding cross-origin-iframe
  // limitation, not a React/portal issue). Instead it stays permanently
  // mounted (see the body-level portal below) and this rAF loop continuously
  // repositions it on top of wherever it should appear - the inline anchor
  // while scrolling, or the popout slot otherwise - by syncing
  // getBoundingClientRect() every frame. This is what makes the handoff
  // actually seamless: the live iframe is never touched, only repainted in
  // place.
  useEffect(() => {
    if (!currentSegment) return
    let rafId: number
    const sync = () => {
      const wrapper = playerWrapperRef.current
      const target = portalTargetRef.current
      if (wrapper && target) {
        const rect = target.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          wrapper.style.display = ''
          wrapper.style.transform = `translate(${rect.left}px, ${rect.top}px)`
          wrapper.style.width = `${rect.width}px`
          wrapper.style.height = `${rect.height}px`
        } else {
          wrapper.style.display = 'none'
        }
      }
      rafId = requestAnimationFrame(sync)
    }
    rafId = requestAnimationFrame(sync)
    return () => cancelAnimationFrame(rafId)
  }, [currentSegment])

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy()
        } catch {
          // ignore
        }
        playerRef.current = null
      }
    }
  }, [])

  // Fast Refresh / HMR preserves useState values across hot reloads but resets
  // useRef. Without this mount-time reset, every dev save while a segment is
  // playing would re-create the YT.Player from preserved currentSegment state
  // and autoplay it. Production has no HMR, so this is effectively a no-op
  // (currentSegment is already null on a true cold mount).
  useEffect(() => {
    setCurrentSegment(null)
    setIsPlayerReady(false)
  }, [])

  // Hydrate persisted width + track viewport. Resize is desktop-only (>= 768px);
  // below that the player is a full-width bottom bar. maxAllowedWidth tracks the
  // viewport so the floating player never overflows a narrow window.
  useEffect(() => {
    try {
      const saved = Number(window.localStorage.getItem(PLAYER_WIDTH_STORAGE_KEY))
      if (Number.isFinite(saved) && saved >= MIN_PLAYER_WIDTH) {
        setPlayerWidth(Math.min(saved, MAX_PLAYER_WIDTH))
      }
    } catch {
      // localStorage unavailable (private mode etc.) - keep the default width.
    }

    const update = () => {
      // Use innerWidth, NOT matchMedia: device-mode emulation reports the
      // emulated viewport via innerWidth, but matchMedia can report the outer
      // window - which made the player float at a 430px emulated width. This
      // also keeps the breakpoint consistent with maxAllowedWidth.
      const vw = window.innerWidth
      setIsDesktop(vw >= 768)
      setMaxAllowedWidth(Math.max(MIN_PLAYER_WIDTH, vw - 32))
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // Drag-resize: pointer listeners live on window so the drag continues even
  // when the cursor leaves the handle. rAF-throttled to avoid relaying-out the
  // iframe more than once per frame.
  useEffect(() => {
    if (!isResizing) return
    let raf = 0
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const { startX, startWidth } = resizeStartRef.current
        // Anchored bottom-right, handle top-left: dragging left (smaller clientX)
        // grows the player. Clamp to bounds and the current viewport width.
        const maxWidth = Math.min(MAX_PLAYER_WIDTH, window.innerWidth - 32)
        const next = Math.min(
          maxWidth,
          Math.max(MIN_PLAYER_WIDTH, startWidth + (startX - e.clientX)),
        )
        setPlayerWidth(next)
      })
    }
    const onUp = () => setIsResizing(false)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [isResizing])

  // Persist the chosen width once a resize ends (not mid-drag).
  useEffect(() => {
    if (isResizing) return
    try {
      window.localStorage.setItem(PLAYER_WIDTH_STORAGE_KEY, String(playerWidth))
    } catch {
      // ignore persistence failures
    }
  }, [isResizing, playerWidth])

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      resizeStartRef.current = { startX: e.clientX, startWidth: playerWidth }
      setIsResizing(true)
    },
    [playerWidth],
  )

  const goFullscreen = useCallback(() => {
    // playerHostRef is replaced in the DOM by the YT iframe on init, so it's
    // detached. Query from the stable wrapper instead.
    const iframe = playerWrapperRef.current?.querySelector('iframe') as HTMLElement & {
      webkitRequestFullscreen?: () => void
    }
    if (!iframe) return
    if (iframe.requestFullscreen) {
      iframe.requestFullscreen().catch(() => {})
    } else if (iframe.webkitRequestFullscreen) {
      iframe.webkitRequestFullscreen()
    }
  }, [])

  const play = useCallback((request: SegmentPlayRequest) => {
    if (!currentSegmentRef.current) {
      setPlayerWidth(Math.min(MAX_PLAYER_WIDTH, window.innerWidth - 32))
    }
    setCurrentSegment(request)
    setIsMinimized(false)
  }, [])

  const api = useMemo<SegmentPlayerApi>(
    () => ({ play, registerAnchor, activePlayerKey }),
    [play, registerAnchor, activePlayerKey],
  )

  const thumbnail = currentSegment
    ? getThumbnail({
        youtubeId: currentSegment.youtubeId,
        url: '',
        thumbnail: (currentSegment.thumbnail ?? undefined) as Media | undefined,
        thumbnailOverride: (currentSegment.thumbnailOverride ?? undefined) as Media | undefined,
      })
    : null

  // VideoPlayer-triggered requests (vs. VideoSegmentChip/VideoAppChip) set
  // segmentName === videoName since there's no distinct "segment" - collapse
  // the label instead of showing "Video - Video".
  const watchingLabel = currentSegment
    ? currentSegment.videoName && currentSegment.videoName !== currentSegment.segmentName
      ? `${currentSegment.segmentName} \u2014 ${currentSegment.videoName}`
      : currentSegment.segmentName
    : ''

  // The live player's host: permanently portaled to document.body and never
  // moved in the DOM again (see the rAF sync effect above) - position/size
  // are driven entirely by that effect writing transform/width/height
  // directly to this node, not by React state, so scroll-tracking isn't
  // throttled by React's render cycle. Portaling to document.body (rather
  // than rendering in place) keeps `position: fixed` resolving against the
  // true viewport even if some ancestor elsewhere in the app applies a CSS
  // transform (which would otherwise hijack fixed positioning).
  const playerHost = (
    <div
      ref={playerWrapperRef}
      className={cn(
        // z-45 in BOTH states. It must stay above the z-40 content stacking
        // context (e.g. `article.overlay` on review pages) or the inline video
        // paints behind the page and shows white until it pops out. The floating
        // popout also stays z-45 so it renders above the z-40 BestVerdict bar.
        // Do NOT lower this below 40 (a prior z-30 tweak caused exactly that bug).
        'fixed left-0 top-0 z-45 overflow-hidden',
        showInline ? 'rounded-xl' : 'rounded-lg',
      )}
      style={{ willChange: 'transform' }}
    >
      {!isPlayerReady && (
        <Icon
          name="spinner"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin text-white"
        />
      )}
      <div ref={playerHostRef} className="absolute inset-0 h-full w-full" />
    </div>
  )

  return (
    <SegmentPlayerProvider api={api}>
      {children}
      {currentSegment && typeof document !== 'undefined' && createPortal(playerHost, document.body)}
      {currentSegment && (
        <>
          <div
            role="dialog"
            aria-label={`Now playing: ${currentSegment.segmentName}`}
            aria-hidden={showInline}
            // Position + width are driven entirely by the isDesktop JS boolean
            // (computed from innerWidth), NOT max-* CSS variants - deterministic
            // and immune to inline-style/ordering overrides. Desktop: floating,
            // resizable, width clamped to viewport. Below 768px: full-width bottom
            // bar (no inline width so left-0/right-0 stretch it).
            style={{
              width: isDesktop && !isMinimized ? Math.min(playerWidth, maxAllowedWidth) : undefined,
            }}
            className={cn(
              'fixed z-45 flex flex-col gap-0 border border-white/5 bg-neutral-900 px-2',
              // The popout chrome (and its video slot) stays mounted even while
              // the live player is rendered inline at its source VideoPlayer -
              // only hidden - so the portal target is always ready the instant
              // the anchor scrolls out of view, with no remount/reload.
              showInline && 'hidden',
              isDesktop
                ? 'bottom-3 right-3 w-[360px] rounded-xl'
                : 'bottom-0 left-0 right-0 rounded-none',
              isDesktop && isMinimized && 'w-auto',
              !isMinimized && 'pb-2',
            )}
          >
            {isDesktop && !isMinimized && (
              <div
                role="button"
                tabIndex={-1}
                aria-label="Resize player"
                title="Drag to resize"
                onPointerDown={onResizePointerDown}
                className="absolute -left-2 -top-2 z-10 flex h-6 w-6 items-center justify-center opacity-25 transition-opacity hover:opacity-100 active:opacity-100"
                style={{ cursor: 'nwse-resize', touchAction: 'none' }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="26"
                  height="26"
                  viewBox="0 0 26 26"
                  fill="none"
                >
                  <path
                    d="M25 1H21C9.95431 1 1 9.95431 1 21V25"
                    className="stroke-black dark:stroke-white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            )}
            <div className="flex items-center justify-between gap-1">
              {isMinimized && (
                <>
                  {thumbnail && (
                    <div className="relative mr-1 h-7 w-11 overflow-hidden rounded-md bg-black">
                      <VideoThumbnailImage
                        key={thumbnail.url}
                        thumbnail={thumbnail}
                        quality="small"
                        alt={watchingLabel}
                        fill
                        sizes="44px"
                        className="relative z-10 opacity-80"
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsMuted((prev) => !prev)}
                    aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                    className="flex h-10 w-6 items-center justify-center text-zinc-400 transition-colors hover:text-white"
                  >
                    <Icon name={isMuted ? 'volume-off' : 'volume-up'} />
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setIsMinimized((prev) => !prev)}
                aria-label={isMinimized ? 'Restore player' : 'Minimize player'}
                className="h-10 min-w-0 flex-1 truncate pl-1 text-left text-12"
              >
                <span className="font-medium text-slate-50/60">Watching:</span>
                <span className="ml-2 font-semibold text-white">{watchingLabel}</span>
              </button>
              <div className="flex shrink-0 items-center">
                <button
                  type="button"
                  onClick={() => setIsMinimized((prev) => !prev)}
                  aria-label={isMinimized ? 'Restore player' : 'Minimize player'}
                  className="flex h-10 w-6 items-center justify-center text-zinc-400 transition-colors hover:text-white"
                >
                  <Icon
                    name={isMinimized ? 'chevron-expand-y' : 'minus'}
                    size={isMinimized ? 13 : 18}
                  />
                </button>
                {!isMinimized && (
                  <button
                    type="button"
                    onClick={goFullscreen}
                    aria-label="Make fullscreen"
                    className="flex h-10 w-6 items-center justify-center text-zinc-400 transition-colors hover:text-white"
                  >
                    <Icon name="chevron-maximize-diagonal" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close player"
                  className="flex h-10 w-6 items-center justify-center text-zinc-400 transition-colors hover:text-white"
                >
                  <Icon name="xmark" />
                </button>
              </div>
            </div>
            <div
              ref={setPopoutSlotEl}
              className={cn(
                'relative aspect-video w-full overflow-hidden rounded-lg bg-black',
                isMinimized && 'hidden',
              )}
            />
          </div>
          {/* Full-screen transparent overlay during a resize drag: stops the
            YouTube iframe from swallowing pointer events mid-drag and keeps the
            resize cursor while dragging anywhere on screen. */}
          {isResizing && (
            <div
              className="fixed inset-0 z-[10000]"
              style={{ cursor: 'nwse-resize' }}
              aria-hidden="true"
            />
          )}
        </>
      )}
    </SegmentPlayerProvider>
  )
}
